import React, { useMemo } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { pushDataLayer } from '../gtm';
import {
  getMateriaByCodigo,
  getMateriasPorAnioOrientacion,
  parseCantidadPendientes
} from '../data/materiasPendientes';

const GRADOS = [
  { anio: '4to', label: '4to año', pendienteField: 'pendientes4to', orientacionField: null },
  { anio: '5to', label: '5to año', pendienteField: 'pendientes5to', orientacionField: 'orientacion5to' },
  { anio: '6to', label: '6to año', pendienteField: 'pendientes6to', orientacionField: 'orientacion6to' }
];

export default function Step7_MateriasEspecificas({ formData, setFormData, next, prev }) {
  const grupos = useMemo(() => {
    return GRADOS.map((grado) => {
      const cantidad = parseCantidadPendientes(formData[grado.pendienteField]);
      const orientacion = grado.orientacionField ? formData[grado.orientacionField] : '';

      return {
        ...grado,
        cantidad,
        orientacion,
        materias: getMateriasPorAnioOrientacion(grado.anio, orientacion)
      };
    }).filter((grupo) => grupo.cantidad > 0);
  }, [formData]);

  const materiasSeleccionadas = formData.materiasPendientesSeleccionadas || {};

  const handleMateriaChange = (grupo, index) => (e) => {
    const materia = getMateriaByCodigo(e.target.value);

    setFormData((prev) => {
      const current = prev.materiasPendientesSeleccionadas || {};
      const currentGroup = Array.isArray(current[grupo.anio])
        ? [...current[grupo.anio]].slice(0, grupo.cantidad)
        : [];

      currentGroup[index] = materia;

      return {
        ...prev,
        materiasPendientesSeleccionadas: {
          ...current,
          [grupo.anio]: currentGroup
        }
      };
    });
  };

  const handleTextoLibreChange = (grupo, index) => (e) => {
    const texto = e.target.value;

    setFormData((prev) => {
      const current = prev.materiasPendientesSeleccionadas || {};
      const currentGroup = Array.isArray(current[grupo.anio])
        ? [...current[grupo.anio]].slice(0, grupo.cantidad)
        : [];

      currentGroup[index] = texto.trim() ? { nombre: texto, codigo: null } : null;

      return {
        ...prev,
        materiasPendientesSeleccionadas: {
          ...current,
          [grupo.anio]: currentGroup
        }
      };
    });
  };

  const buildNormalizedSelection = () => {
    return grupos.reduce((acc, grupo) => {
      const selectedForGroup = Array.isArray(materiasSeleccionadas[grupo.anio])
        ? materiasSeleccionadas[grupo.anio]
        : [];

      acc[grupo.anio] = selectedForGroup
        .slice(0, grupo.cantidad)
        .filter(Boolean);

      return acc;
    }, {});
  };

  const handleNext = () => {
    const normalizedSelection = buildNormalizedSelection();
    setFormData((prev) => ({
      ...prev,
      materiasPendientesSeleccionadas: normalizedSelection
    }));

    pushDataLayer('paso8_modalidad');
    next({ materiasPendientesSeleccionadas: normalizedSelection });
  };

  return (
    <div className="form-container materias-especificas-step">
      <h2 className="form-title">¿Qué materias tenés pendientes?</h2>
      <p style={{ fontSize: "12px", textAlign: "center" }}>
        Si las recordás, seleccioná las materias específicas. Si no, podés continuar sin completar este paso.
      </p>

      <Form>
        {grupos.length === 0 ? (
          <Alert variant="info">
            No hay materias pendientes para seleccionar con las respuestas indicadas.
          </Alert>
        ) : (
          grupos.map((grupo) => {
            const selectedForGroup = Array.isArray(materiasSeleccionadas[grupo.anio])
              ? materiasSeleccionadas[grupo.anio]
              : [];
            const selectedCodes = selectedForGroup
              .map((materia) => materia?.codigo)
              .filter(Boolean);
            const orientationLabel = grupo.orientacion ? ` - ${grupo.orientacion}` : '';

            return (
              <div key={grupo.anio} className="mb-4">
                <h3 className="materias-especificas-title">
                  {grupo.label}{orientationLabel}
                </h3>

                {grupo.materias.length === 0 ? (
                  Array.from({ length: grupo.cantidad }).map((_, index) => {
                    const entrada = selectedForGroup[index];
                    const valorTexto = entrada?.nombre || '';

                    return (
                      <Form.Group className="mb-3" key={`${grupo.anio}-libre-${index}`}>
                        <Form.Label>Materia {index + 1} <span style={{ fontWeight: 'normal', fontSize: '12px' }}>(opcional)</span></Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Escribí el nombre de la materia"
                          value={valorTexto}
                          onChange={handleTextoLibreChange(grupo, index)}
                        />
                      </Form.Group>
                    );
                  })
                ) : (
                  Array.from({ length: grupo.cantidad }).map((_, index) => {
                    const selectedCodigo = selectedForGroup[index]?.codigo || '';

                    return (
                      <Form.Group className="mb-3" key={`${grupo.anio}-${index}`}>
                        <Form.Label>Materia {index + 1}</Form.Label>
                        <Form.Select
                          value={selectedCodigo}
                          onChange={handleMateriaChange(grupo, index)}
                        >
                          <option value="">No recuerdo / prefiero omitir</option>
                          {grupo.materias.map((materia) => (
                            <option
                              key={materia.codigo}
                              value={materia.codigo}
                              disabled={selectedCodes.includes(materia.codigo) && selectedCodigo !== materia.codigo}
                            >
                              {materia.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    );
                  })
                )}
              </div>
            );
          })
        )}

        <div className="d-flex justify-content-between">
          <Button variant="secondary" onClick={prev}>Atrás</Button>
          <Button variant="primary" onClick={handleNext}>Aceptar</Button>
        </div>
      </Form>
    </div>
  );
}
